import { agentDisplayName } from '@/config/env'

/** Subset of the relay `/api/billing/usage/*` responses the UI consumes.
 *
 * The relay response carries more fields (USD totals, lifetime amount,
 * monthly_adjustment_id, etc.); declare only what the page actually reads
 * so the type stays a contract for what's load-bearing.
 *
 * All numeric fields arrive as strings (Pydantic Decimal → JSON string),
 * except counts the relay types as `int` — those arrive as JSON numbers.
 */

/** A month add-on on the receipt: a fraction of the month's usage total. */
export interface BillingAddonLine {
  addon: 'advanced_support' | 'project_rollout'
  /** Fraction of the month's usage total charged: "0.1" is 10%. */
  fraction: string
  amount: string
  /** The amount at the month's minute rate, drawn from the balance. */
  minutes: string
}

/** Which minutes-per-person-day setting the full-time-worker estimate divides
 *  by: one of three named tiers, or a number staff typed in. */
export type FtweTier = 'high' | 'med' | 'low' | 'custom'

/** The minutes of agent work that count as one person-day, for each named
 *  tier. A bigger number means fewer FTWEs. */
export const FTWE_TIERS: Record<Exclude<FtweTier, 'custom'>, number> = {
  high: 143,
  med: 191,
  low: 286,
}

export const FTWE_TIER_LABELS: Record<FtweTier, string> = {
  high: 'High',
  med: 'Med',
  low: 'Low',
  custom: 'Custom',
}

/** The tiers in the order the change dialog offers them. */
export const FTWE_TIER_ORDER: readonly FtweTier[] = [
  'high',
  'med',
  'low',
  'custom',
]

/** What the rate card, its change dialog and the change preview call the FTWE
 *  setting. */
export const FTWE_LABEL = 'FTWE multiplier'

/** The org's active rate card as relay's snapshot carries it, which the
 *  change dialog prefills from. Decimals arrive as strings. */
export interface RateCardSnapshot {
  minute_rate: string | null
  reasoning_multiplier: string | null
  non_conversation_discount: string | null
  computer_use_multiplier: string | null
  david_session_multiplier: string | null
  document_rate?: string | null
  rubric_eval_multiplier?: string | null
  ftwe_tier: FtweTier | null
  ftwe_multiplier: string | null
  currency: string
}

export const RATE_FIELDS = [
  { key: 'minute_rate', label: 'Minute rate ($/min)' },
  { key: 'non_conversation_discount', label: 'Non-conversation discount' },
  { key: 'reasoning_multiplier', label: 'Reasoning multiplier' },
  { key: 'computer_use_multiplier', label: 'Computer-use multiplier' },
  { key: 'david_session_multiplier', label: 'David-session multiplier' },
  { key: 'document_rate', label: 'Document rate ($/document)' },
  { key: 'rubric_eval_multiplier', label: 'Rubric-evaluation multiplier' },
] as const

export type RateField = (typeof RATE_FIELDS)[number]['key']

export type RateCardChangeRequest = {
  /** YYYY-MM-DD */
  effective_from: string
  note?: string
  confirm: boolean
  /** On confirm: the version of the card the preview was made against. */
  previewed_card_version?: string
  /** The FTWE setting to move to; left out, the card's setting carries over. */
  ftwe_tier?: FtweTier
  /** The minutes per person-day of a `custom` tier. */
  ftwe_multiplier?: number
} & Partial<Record<RateField, number>>

/** The server's answer to a rate card change: the preview when it awaits
 *  confirmation, the written card when applied. Rates arrive as strings. */
export interface RateCardChange {
  status: 'awaiting_confirmation' | 'applied'
  org_slug: string
  effective_from: string
  current: Record<RateField, string>
  proposed: Record<RateField, string>
  ftwe_current: { tier: FtweTier; multiplier: string }
  ftwe_proposed: { tier: FtweTier; multiplier: string }
  impact: Record<string, string> | null
  /** The card the preview was made against, as it read then; a confirmation names it. */
  card_in_force_version: string
  consequences: string[]
  card_id?: string
}

/** One rate a new card moved partway through a billing period. Decimals
 *  arrive as strings. */
export interface RateChange {
  /** ISO timestamp the new card took over. */
  effective_from: string
  field: RateField
  before: string
  after: string
}

/** The full-time-worker estimate relay computes for a billing period. A null
 *  `tier`, `multiplier` and `estimate` mean the org has no rate card, so
 *  there is nothing to estimate from. Decimals arrive as strings. */
export interface FtweEstimate {
  tier: FtweTier | null
  multiplier: string | null
  ai_minutes: string
  non_conversation_minutes: string
  /** Where the phone-line minutes came from: the month's close, a Twilio
   *  pull, or nowhere. */
  twilio_source: 'close' | 'pull' | 'none'
  calendar_days: number
  estimate: string | null
}

export interface CurrentUsageResponse {
  convo_minutes: string
  analysis_minutes: string
  computer_use_minutes: string
  /** Ledger-truth USD per line for the in-flight month (SUM of amount),
   *  so the receipt reads amounts instead of re-deriving minutes × rate. */
  conversation_amount: string
  analysis_amount: string
  computer_use_amount: string
  twilio_pulled_at: string | null
  /** Month-to-date Twilio minutes from the relay's nightly sync; only
   *  meaningful when `twilio_pulled_at` is set. */
  twilio_minutes?: string | null
  period_start_balance_minutes: string
  period_credits_added_minutes: string
  period_end_balance_minutes: string
  period_used_minutes: string
  /** Comp credits we *gave* (credit_compensation only, not purchases) in the
   *  period — for the receipt's Compensation credits line. USD amount is <= 0. */
  period_compensation_credits: string
  period_compensation_credits_minutes: string
  rate_card: RateCardSnapshot
  /** First-of-month "YYYY-MM-DD" for the earliest ledger entry the org has,
   *  or null if the org has no ledger rows yet. Drives the period selector's
   *  lower bound. */
  earliest_period_start: string | null
  /** YYYY-MM-DD: the first day relay accepts as a credit's date this month.
   *  The credit dialog's date picker goes back no further. */
  earliest_credit_date: string
  /** Number of billed calls in the period so far. Null/absent when the relay
   *  has no count for the period — the table leaves the Count cell blank
   *  rather than showing a misleading 0. */
  call_count?: string | null
  /** Number of billed agent sessions in the period so far. Null/absent when
   *  the relay has no count for the period — the table leaves the Count cell
   *  blank rather than showing a misleading 0. */
  session_count?: string | null
  /** David and Hanna turns billed this month: amount is each turn's token
   *  cost marked up by the rate card's david_session_multiplier, and minutes
   *  are that amount at the card's minute rate — a charge, not time spent. */
  david_session_minutes: string
  david_session_amount: string
  document_amount?: string
  document_minutes?: string
  document_count?: number
  /** Calls graded against the org's rubrics after they ended: amount is the
   *  ledger's charge for the grading, and minutes are that amount at the
   *  card's minute rate. */
  rubric_eval_minutes?: string
  rubric_eval_amount?: string
  rubric_eval_count?: number
  /** Number of billed assistant turns in the period. Null/absent when the
   *  relay has no count — the table leaves the Count cell blank. */
  david_session_count?: string | null
  /** Add-ons switched on for this month, priced on the usage so far. Absent
   *  from a relay that predates add-ons, which bills none. The relay prices
   *  these on usage so far, which has no Connection yet. At close they are
   *  priced on usage including Connection, so they rise. */
  addons?: BillingAddonLine[]
  /** Each rate a new card moved during the period, oldest first. */
  rate_changes: RateChange[]
  ftwe: FtweEstimate
}

export interface MonthEndUsageResponse {
  period_start: string
  period_end: string
  twilio_finalized_at: string
  conversation_amount: string
  analysis_amount: string
  twilio_adjustment_amount: string
  computer_use_amount: string
  total_amount: string
  conversation_minutes: string
  analysis_minutes: string
  twilio_adjustment_minutes: string
  computer_use_minutes: string
  total_minutes: string
  /** Rate of the card in force at the month's last moment, which priced its
   *  Connection and round-up. "0" when no card was in force then. The line
   *  minutes are the ledger's own; a month whose rate changed partway
   *  through bills a line at two rates, so the receipt prices each line at
   *  its amount over its minutes. */
  minute_rate: string
  period_start_balance_minutes: string
  period_credits_added_minutes: string
  period_end_balance_minutes: string
  period_used_minutes: string
  period_compensation_credits: string
  period_compensation_credits_minutes: string
  /** Number of billed calls in the closed period. Null/absent for months
   *  closed before counts were captured — the table leaves the Count cell
   *  blank rather than showing a misleading 0. */
  call_count?: string | null
  /** Number of billed agent sessions in the closed period. Null/absent for
   *  months closed before counts were captured — the table leaves the Count
   *  cell blank rather than showing a misleading 0. */
  session_count?: string | null
  /** David and Hanna turns billed in the closed period. Zero for months that
   *  closed before assistant billing existed. */
  david_session_minutes: string
  david_session_amount: string
  document_amount?: string
  document_minutes?: string
  document_count?: number
  rubric_eval_minutes?: string
  rubric_eval_amount?: string
  rubric_eval_count?: number
  david_session_count?: string | null
  /** Add-ons the close charged for the month, already in `total_amount`.
   *  Absent from a relay that predates add-ons, which bills none. */
  addons?: BillingAddonLine[]
  /** Each rate a new card moved during the period, oldest first. */
  rate_changes: RateChange[]
  ftwe: FtweEstimate
}

export interface PeriodOption {
  /** YYYY-MM, e.g. "2026-04" */
  value: string
  /** Display label, e.g. "April 2026 (current)" */
  label: string
  /** True for the in-progress period (no monthly_twilio_adjustment yet). */
  isCurrent: boolean
}

/** One duty's share of a period's charges, from
 *  `/api/billing/usage/duties`. Covers what reaches a duty on its own —
 *  conversation and analysis through the call, computer use through the
 *  session, rubric grading through the graded call. Connection is an org-wide residual with no duty. */
export interface DutyCallCost {
  /** Null on the row collecting calls that cannot name a duty. */
  duty_id: string | null
  duty_name: string | null
  call_count: number
  session_count: number
  conversation_amount: string
  analysis_amount: string
  computer_use_amount: string
  rubric_eval_amount: string
  rubric_eval_count: number
}

/** Only `duties` is declared: the page builds its own totals by
 *  apportioning the receipt's lines, so the response's own totals would be a
 *  second, unreconciled source for the same numbers. */
export interface DutyCallCostsResponse {
  duties: DutyCallCost[]
}

/** Row label for calls that carry no duty. */
export const UNATTRIBUTED_DUTY_LABEL = 'Unattributed'

/** Canonical row-label strings shared between `useBillingPageData` (which
 *  assigns them as `UtilizationLine.description`) and `UtilizationTable`'s
 *  `ROW_TOOLTIPS` (which keys tooltip copy off the same strings). Referencing
 *  these constants from both sides means a rename is a single edit instead of
 *  two literals that can silently drift apart. */
export const BILLING_ROW_LABELS = {
  conversation: 'Conversation',
  analysis: 'Analysis',
  connection: 'Connection',
  projectedConnection: 'Connection (estimate)',
  computerSessions: 'Computer sessions',
  documents: 'Documents',
  evaluationRubrics: 'Evaluation Rubrics',
  davidSessions: `${agentDisplayName('david')} Sessions`,
  advancedSupport: 'Advanced Support',
  projectRollout: 'Project Rollout',
} as const

export interface UtilizationLine {
  description: string
  quantityMinutes: number
  pricePerMinute: number
  amountUsd: number
  /** Unit count for the line — calls on the Calls row, sessions on the
   *  Computer sessions row. Absent on rows where a count doesn't apply
   *  (sub-rows, Credits) or when no count was captured for the period;
   *  the table leaves the Count cell blank. */
  count?: number
  /** Stable identity for rows whose description is not unique. Duty names
   *  are user-controlled, so two rows can carry the same one; the table
   *  keys collapse state and DOM ids off this where it is present. */
  rowId?: string
  /** Present on a grouped line (e.g. "Call"): the parent's quantity/amount are
   *  the sum of these. The table renders them as indented sub-rows. */
  subRows?: UtilizationLine[]
  /** A credit line (credits we gave): amount is <= 0 and nets out of the Total,
   *  but its minutes are excluded from the minutes total. */
  isCredit?: boolean
  /** The usage the add-ons below it are a percentage of. It restates the
   *  lines above rather than adding to them, so the totals leave it out. */
  isSubtotal?: boolean
  /** Present on a month add-on: the fraction of the month's usage it charges.
   *  The row has no count of its own; its minutes are what it takes from the
   *  balance, and it prices itself as this percentage rather than a
   *  per-minute rate. */
  usageFraction?: number
  /** An estimate of a charge month close has yet to bill. It counts in its
   *  parent's sums and the totals as a billed line does; the Minutes Ledger
   *  adds it to Used, and the By duty view keeps it as one top-level row. */
  isProjected?: boolean
}

/** An amount of the bill in both of its units: dollars and the minutes they
 *  take from the balance. */
export interface UsdAndMinutes {
  usd: number
  minutes: number
}

export interface MinutesLedgerSummary {
  startMinutes: number
  addedMinutes: number
  usedMinutes: number
  remainingMinutes: number
}

/** The two ledger entry types that add minutes to an org's balance. */
export type CreditEntryType = 'credit_purchase' | 'credit_compensation'

/** One purchase or compensation credit added in the period, from
 *  `/api/billing/usage/credits`, oldest first. */
export interface BillingCredit {
  id: string
  entry_type: CreditEntryType
  /** ISO timestamp the credit counts toward the balance from. */
  created_at: string
  minutes: string
  /** USD in the ledger's sign: zero or negative for a credit. */
  amount: string
  /** The invoices the credit's note names, in order: usually none for a
   *  compensation, and none for a note that names no invoice relay can read. */
  invoice_numbers: string[]
}

/** Only `credits` is declared: the page already knows the period it asked
 *  for. Their minutes sum to the period's `period_credits_added_minutes`. */
export interface BillingCreditsResponse {
  credits: BillingCredit[]
}

/** Body of `POST /billing/credits`. With `dry_run` the relay answers with the
 *  plan and writes nothing. `minutes` is a decimal string, as the relay
 *  parses it. For a compensation the reason travels in `note`. */
export interface BillingCreditRequest {
  org_slug: string
  credit_type: 'purchase' | 'compensation'
  minutes: string
  /** The invoice Total in dollars; a purchase only. */
  amount?: string
  /** YYYY-MM-DD, within the current UTC month. */
  credit_date: string
  invoice_number?: string
  note?: string
  dry_run: boolean
}

/** What the relay will write (or wrote) for a credit request, priced at the
 *  org's rate card. Decimals arrive as strings. */
export interface BillingCreditPlan {
  org_slug: string
  credit_type: 'purchase' | 'compensation'
  entry_type: CreditEntryType
  credit_date: string
  minutes: string
  minute_rate: string
  /** USD in the ledger's sign: zero or negative for a credit. */
  amount: string
  currency: string
  rate_card_id: string | null
  /** What the minutes come to at the rate card; null when no amount was
   *  entered to compare against. */
  rate_card_amount: string | null
  note: string
  created_by: string
}

/** Answer to `POST /billing/credits`: `credit` is the ledger row written,
 *  null on a dry run. */
export interface BillingCreditChange {
  dry_run: boolean
  plan: BillingCreditPlan
  credit: BillingCredit | null
}
